<template>
  <div class="country-cloud">
    <div v-for="(c, i) in topCountriesRef" :key="c.value" :class="flagClasses(c, i)"></div>
  </div>
</template>

<style lang="scss" scoped>
.country-cloud {
  position: relative;
  height: 150px;
  width: 180px;
}

.flag {
  position: absolute;
  height: 42px;
  width: 54px;
  z-index: 0;
  box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.2);

  &.size-0 {
    transform: translate(-50%, -50%) scale(0.6);
  }
  &.size-1 {
    transform: translate(-50%, -50%) scale(0.8);
  }
  &.size-2 {
    transform: translate(-50%, -50%) scale(1.0);
  }
  &.size-3 {
    transform: translate(-50%, -50%) scale(1.2);
  }

  &.pos-0 {
    top: 50%;
    left: 50%;
    z-index: 2;
  }
  &.pos-1 {
    top: 30%;
    left: 60%;
  }
  &.pos-2 {
    top: 40%;
    left: 80%;
  }
  &.pos-3 {
    top: 70%;
    left: 70%;
  }
  &.pos-4 {
    top: 80%;
    left: 55%;
  }
  &.pos-5 {
    top: 70%;
    left: 30%;
  }
  &.pos-6 {
    top: 50%;
    left: 20%;
  }
  &.pos-7 {
    top: 35%;
    left: 35%;
    z-index: 1;
  }
  &.pos-8 {
    top: 20%;
    left: 25%;
  }
}
</style>

<script lang="ts" setup>
import { computed } from 'vue'
import { ValueCount } from '@/rd/ResearchDataModel'
import { useExploreStore } from '@/explore/ExploreStore'
import { useViewScopeStore } from '../ViewScopeStore'
import { usePlaceAnalysis } from './PlaceAnalysis'
import { getTopValues } from '@/util/AnalysisUtil'
import { LoadMode } from '@/util/AsyncData'

const positionSequence = [0, 3, 5, 7, 1, 2, 4, 6, 8]

const props = defineProps<{
  countries?: ValueCount[]
}>()

const { getScopeCountryCodes } = usePlaceAnalysis()
const exploreStore = useExploreStore()
const viewScopeStore = useViewScopeStore()

const scopeInfoRef = computed(() =>
  props.countries ? undefined : viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.data,
)

const topCountriesRef = computed(() => {
  if (props.countries) {
    return props.countries.slice(0, 9)
  }
  if (scopeInfoRef.value) {
    const countryCodes = [...getScopeCountryCodes(scopeInfoRef.value)]
    return getTopValues(countryCodes, 10)
  }
  return []
})

const maxCount = computed(() => Math.max(...topCountriesRef.value.map(c => c.count), 1))

function flagClasses(c: ValueCount, index: number) {
  const size = Math.round((c.count / maxCount.value) * 3) // sizes: 0-3
  const posIndex = positionSequence[index]
  return ['flag', 'fi', `fi-${c.value.toLowerCase()}`, `pos-${posIndex}`, `size-${size}`]
}
</script>
